/** Formato y semántica visual para KPIs de Adopción. */

export function formatRatioPct(
  ratio: number | null | undefined,
  locale: string,
  digits = 1,
): string {
  if (ratio == null || Number.isNaN(ratio)) return '—';
  return `${(ratio * 100).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

export function formatSignedRatioPct(
  ratio: number | null | undefined,
  locale: string,
  digits = 1,
): string {
  if (ratio == null || Number.isNaN(ratio)) return '—';
  const pct = ratio * 100;
  const formatted = Math.abs(pct).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (pct > 0) return `+${formatted}%`;
  if (pct < 0) return `−${formatted}%`;
  return `${formatted}%`;
}

export function formatCount(value: number | null | undefined, locale: string): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString(locale);
}

/** Near zero: |ratio| < 1%. Sign drives warning (overstock vs stockout). */
export function biasVariant(
  ratio: number | null | undefined,
): 'default' | 'warning' {
  if (ratio == null || Number.isNaN(ratio)) return 'default';
  if (Math.abs(ratio) < 0.01) return 'default';
  return 'warning';
}

export function biasDirectionKey(
  ratio: number | null | undefined,
): 'neutral' | 'up' | 'down' {
  if (ratio == null || Number.isNaN(ratio) || Math.abs(ratio) < 0.01) return 'neutral';
  return ratio > 0 ? 'up' : 'down';
}

/** Color de celda para deltas firmados: + sobrestock, − rotura. */
export function signedDeltaClassName(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value) || value === 0) return 'text-gray-700';
  if (value > 0) return 'font-semibold text-orange-600';
  return 'font-semibold text-sky-700';
}

export function formatSignedNumber(
  value: number | null | undefined,
  locale: string,
  digits = 0,
): string {
  if (value == null || Number.isNaN(value)) return '—';
  const formatted = Math.abs(value).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}

export interface LinesBiasSummary {
  deltaUnits: number | null;
  deltaAmount: number | null;
  biasRatio: number | null;
  linesTotal: number;
  linesWithDelta: number;
  linesWithAmount: number;
  sumSuggested: number;
}

/** Agrega sesgo total (ERP − sugerida) sobre un conjunto de líneas. */
export function summarizeLinesBias(lines: UsageLineRowLike[]): LinesBiasSummary {
  let deltaUnits = 0;
  let hasDelta = false;
  let deltaAmount = 0;
  let hasAmount = false;
  let sumSuggested = 0;
  let sumErp = 0;
  let biasLines = 0;
  let linesWithAmount = 0;

  for (const line of lines) {
    const suggested = line.qty_suggested;
    const erp = line.qty_erp;
    const delta = line.delta_total;
    if (delta != null && !Number.isNaN(delta)) {
      deltaUnits += delta;
      hasDelta = true;
    }
    if (line.delta_total_amount != null && !Number.isNaN(line.delta_total_amount)) {
      deltaAmount += line.delta_total_amount;
      hasAmount = true;
      linesWithAmount += 1;
    }
    if (
      suggested != null &&
      suggested > 0 &&
      erp != null &&
      !Number.isNaN(suggested) &&
      !Number.isNaN(erp)
    ) {
      sumSuggested += suggested;
      sumErp += erp;
      biasLines += 1;
    }
  }

  return {
    deltaUnits: hasDelta ? deltaUnits : null,
    deltaAmount: hasAmount ? deltaAmount : null,
    biasRatio: sumSuggested > 0 ? (sumErp - sumSuggested) / sumSuggested : null,
    linesTotal: lines.length,
    linesWithDelta: biasLines,
    linesWithAmount,
    sumSuggested,
  };
}

interface UsageLineRowLike {
  qty_suggested: number | null;
  qty_erp: number | null;
  delta_total: number | null;
  delta_total_amount: number | null;
}
